import type { IngestRequest, ReadingSample } from '../types/domain'
import { crc32Update } from '../ble/hubProtocol'

const DB_NAME = 'novaroll-monitor'
const DB_VERSION = 2
const READINGS = 'readings'
const OPERATIONS = 'operations'
const HUB_FILES = 'hubFiles'
const HUB_CHUNKS = 'hubChunks'

export interface HubSessionRef {
  palletId: string
  serialNumber: string
  sessionId: string
  sessionToken: number
}

export interface HubFileMeta extends HubSessionRef {
  key: string
  fileId: number
  fileName: string
  committedOffset: number
  fileSize: number
  fileCrc32: number
  verified: boolean
  updatedAt: string
}

interface HubChunk {
  key: string
  fileKey: string
  offset: number
  bytes: Uint8Array
}

export interface LocalReading extends ReadingSample {
  id: string
  clientSessionId: string
  palletId: string
  sensorId: string
  uploadedAt?: string
}

export interface SyncOperation {
  operationId: string
  kind: 'MEASUREMENT_INGEST'
  payload: IngestRequest
  state: 'PENDING' | 'SENDING' | 'CONFLICT'
  attempts: number
  createdAt: string
  lastError?: string
}

let databasePromise: Promise<IDBDatabase> | undefined

function database(): Promise<IDBDatabase> {
  databasePromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, DB_VERSION)
    open.onupgradeneeded = () => {
      const db = open.result
      if (!db.objectStoreNames.contains(READINGS)) {
        const readings = db.createObjectStore(READINGS, { keyPath: 'id' })
        readings.createIndex('bySession', 'clientSessionId', { unique: false })
        readings.createIndex('byPallet', 'palletId', { unique: false })
      }
      if (!db.objectStoreNames.contains(OPERATIONS)) {
        const operations = db.createObjectStore(OPERATIONS, { keyPath: 'operationId' })
        operations.createIndex('byState', 'state', { unique: false })
      }
      if (!db.objectStoreNames.contains(HUB_FILES)) db.createObjectStore(HUB_FILES, { keyPath: 'key' })
      if (!db.objectStoreNames.contains(HUB_CHUNKS)) {
        const chunks = db.createObjectStore(HUB_CHUNKS, { keyPath: 'key' })
        chunks.createIndex('byFile', 'fileKey', { unique: false })
      }
    }
    open.onsuccess = () => resolve(open.result)
    open.onerror = () => reject(open.error ?? new Error('Не удалось открыть локальную базу'))
    open.onblocked = () => reject(new Error('Закройте другие вкладки приложения и повторите открытие локальной базы'))
  }).catch(error => {
    databasePromise = undefined
    throw error
  })
  return databasePromise
}

export function hubFileKey(ref: HubSessionRef, fileId: number): string {
  return JSON.stringify([ref.palletId, ref.serialNumber, ref.sessionId, ref.sessionToken, fileId])
}

export async function getHubFileMeta(ref: HubSessionRef, fileId: number): Promise<HubFileMeta | undefined> {
  const db = await database()
  const tx = db.transaction(HUB_FILES, 'readonly')
  const meta = await result(tx.objectStore(HUB_FILES).get(hubFileKey(ref, fileId))) as HubFileMeta | undefined
  await complete(tx)
  return meta
}

export async function recentHubSession(palletId: string): Promise<{ ref: HubSessionRef; files: HubFileMeta[] } | undefined> {
  const db = await database()
  const tx = db.transaction(HUB_FILES, 'readonly')
  const all = await result(tx.objectStore(HUB_FILES).getAll()) as HubFileMeta[]
  await complete(tx)
  const latest = all.filter(file => file.palletId === palletId)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0]
  if (!latest) return undefined
  const files = all.filter(file => file.palletId === palletId && file.serialNumber === latest.serialNumber
    && file.sessionId === latest.sessionId && file.sessionToken === latest.sessionToken)
    .sort((a, b) => a.fileId - b.fileId)
  return {
    ref: { palletId, serialNumber: latest.serialNumber, sessionId: latest.sessionId, sessionToken: latest.sessionToken },
    files,
  }
}

export async function commitHubBlock(
  ref: HubSessionRef,
  fileId: number,
  fileName: string,
  offset: number,
  fileSize: number,
  fileCrc32: number,
  bytes: Uint8Array,
): Promise<HubFileMeta> {
  const key = hubFileKey(ref, fileId)
  const current = await getHubFileMeta(ref, fileId)
  if (offset !== (current?.committedOffset ?? 0)) throw new Error('Локальное смещение файла изменилось во время передачи')
  if (current && (current.fileSize !== fileSize || current.fileCrc32 !== fileCrc32)) {
    throw new Error('Хаб изменил размер или CRC файла ранее сохранённого сеанса')
  }
  if (offset + bytes.length > fileSize || (bytes.length === 0 && offset !== fileSize)) {
    throw new Error('Некорректная длина сохранённого блока')
  }
  const meta: HubFileMeta = {
    ...ref, key, fileId, fileName, committedOffset: offset + bytes.length,
    fileSize, fileCrc32, verified: false, updatedAt: new Date().toISOString(),
  }
  const db = await database()
  const tx = db.transaction([HUB_FILES, HUB_CHUNKS], 'readwrite')
  if (bytes.length) tx.objectStore(HUB_CHUNKS).put({
    key: `${key}:${String(offset).padStart(10, '0')}`, fileKey: key, offset, bytes: Uint8Array.from(bytes),
  } satisfies HubChunk)
  tx.objectStore(HUB_FILES).put(meta)
  await complete(tx)
  return meta
}

async function hubChunks(key: string): Promise<HubChunk[]> {
  const db = await database()
  const tx = db.transaction(HUB_CHUNKS, 'readonly')
  const chunks = await result(tx.objectStore(HUB_CHUNKS).index('byFile').getAll(IDBKeyRange.only(key))) as HubChunk[]
  await complete(tx)
  return chunks.sort((left, right) => left.offset - right.offset)
}

export async function verifyHubFile(ref: HubSessionRef, fileId: number): Promise<HubFileMeta> {
  const meta = await getHubFileMeta(ref, fileId)
  if (!meta || meta.committedOffset !== meta.fileSize) throw new Error('Файл хаба ещё не получен целиком')
  const db = await database()
  const tx = db.transaction(HUB_CHUNKS, 'readonly')
  const txDone = complete(tx)
  void txDone.catch(() => undefined)
  const { offset, state } = await new Promise<{ offset: number; state: number }>((resolve, reject) => {
    let state = 0xffffffff
    let offset = 0
    const request = tx.objectStore(HUB_CHUNKS).index('byFile').openCursor(IDBKeyRange.only(meta.key))
    request.onerror = () => reject(request.error ?? new Error('Не удалось проверить файл хаба'))
    request.onsuccess = () => {
      const cursor = request.result
      if (!cursor) { resolve({ offset, state }); return }
      const chunk = cursor.value as HubChunk
      if (chunk.offset !== offset) { reject(new Error('В локальном файле хаба обнаружен пропуск')); return }
      state = crc32Update(state, chunk.bytes)
      offset += chunk.bytes.length
      cursor.continue()
    }
  })
  await txDone
  if (offset !== meta.fileSize || ((state ^ 0xffffffff) >>> 0) !== meta.fileCrc32) {
    throw new Error(`CRC-32 файла ${meta.fileName} не совпадает`)
  }
  meta.verified = true
  const update = db.transaction(HUB_FILES, 'readwrite')
  update.objectStore(HUB_FILES).put(meta)
  await complete(update)
  return meta
}

export async function resetHubFile(ref: HubSessionRef, fileId: number): Promise<void> {
  const key = hubFileKey(ref, fileId)
  const db = await database()
  const tx = db.transaction([HUB_FILES, HUB_CHUNKS], 'readwrite')
  tx.objectStore(HUB_FILES).delete(key)
  const cursor = tx.objectStore(HUB_CHUNKS).index('byFile').openCursor(IDBKeyRange.only(key))
  cursor.onsuccess = () => {
    if (!cursor.result) return
    cursor.result.delete()
    cursor.result.continue()
  }
  await complete(tx)
}

export async function getHubFileBlob(ref: HubSessionRef, fileId: number): Promise<Blob> {
  const meta = await getHubFileMeta(ref, fileId)
  if (!meta?.verified) throw new Error('Файл ещё не проверен')
  const chunks = await hubChunks(meta.key)
  return new Blob(chunks.map(chunk => Uint8Array.from(chunk.bytes)), { type: 'text/csv;charset=utf-8' })
}

function complete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error('Ошибка локальной транзакции'))
    transaction.onabort = () => reject(transaction.error ?? new Error('Локальная транзакция отменена'))
  })
}

function result<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('Ошибка локального запроса'))
  })
}

async function checksum(samples: ReadingSample[]): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(samples))
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return `sha256:${Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')}`
}

export async function saveReadSession(
  palletId: string,
  sensorId: string,
  startedAt: string,
  samples: ReadingSample[],
): Promise<IngestRequest> {
  if (samples.length === 0) throw new Error('Датчик не вернул измерений')
  const clientSessionId = crypto.randomUUID()
  const operationId = crypto.randomUUID()
  const completedAt = samples.at(-1)?.measuredAt ?? new Date().toISOString()
  const payload: IngestRequest = {
    operationId,
    clientSessionId,
    palletId,
    sensorId,
    startedAt,
    completedAt,
    checksum: await checksum(samples),
    samples,
  }
  const db = await database()
  const tx = db.transaction([READINGS, OPERATIONS], 'readwrite')
  const readingStore = tx.objectStore(READINGS)
  samples.forEach(sample => readingStore.put({
    ...sample,
    id: `${clientSessionId}:${sample.sequenceNo}:${sample.channel}`,
    clientSessionId,
    palletId,
    sensorId,
  } satisfies LocalReading))
  tx.objectStore(OPERATIONS).put({
    operationId,
    kind: 'MEASUREMENT_INGEST',
    payload,
    state: 'PENDING',
    attempts: 0,
    createdAt: new Date().toISOString(),
  } satisfies SyncOperation)
  await complete(tx)
  return payload
}

export async function pendingOperations(): Promise<SyncOperation[]> {
  const db = await database()
  const tx = db.transaction(OPERATIONS, 'readonly')
  const values = await result(tx.objectStore(OPERATIONS).getAll()) as SyncOperation[]
  await complete(tx)
  return values.filter(value => value.state === 'PENDING' || value.state === 'SENDING' || value.state === 'CONFLICT')
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

export async function pendingCount(): Promise<number> {
  return (await pendingOperations()).filter(operation => operation.state !== 'CONFLICT').length
}

export async function reconcilePendingCatalog(validPalletIds: Set<string>, validSensorIds: Set<string>): Promise<number> {
  const operations = await pendingOperations()
  let changed = 0
  for (const operation of operations) {
    if (validPalletIds.has(operation.payload.palletId) && validSensorIds.has(operation.payload.sensorId)) continue
    operation.state = 'CONFLICT'
    operation.lastError = 'Локальный пакет относится к предыдущей демо-базе. Повторная HTTP-отправка остановлена.'
    await updateOperation(operation)
    changed += 1
  }
  return changed
}

export async function updateOperation(operation: SyncOperation): Promise<void> {
  const db = await database()
  const tx = db.transaction(OPERATIONS, 'readwrite')
  tx.objectStore(OPERATIONS).put(operation)
  await complete(tx)
}

export async function completeOperation(operation: SyncOperation): Promise<void> {
  const db = await database()
  const tx = db.transaction([READINGS, OPERATIONS], 'readwrite')
  tx.objectStore(OPERATIONS).delete(operation.operationId)
  const readingIndex = tx.objectStore(READINGS).index('bySession')
  const cursorRequest = readingIndex.openCursor(IDBKeyRange.only(operation.payload.clientSessionId))
  cursorRequest.onsuccess = () => {
    const cursor = cursorRequest.result
    if (!cursor) return
    cursor.update({ ...(cursor.value as LocalReading), uploadedAt: new Date().toISOString() })
    cursor.continue()
  }
  await complete(tx)
}
